import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { AssetNodeType } from '../models';
import { Button, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import UserGroupPermissions, {
  archivePermissionRefs,
  editPermissionRefs,
} from '../../../core/components/UserGroupPermissions';

const nodeTypeColors = {
  [AssetNodeType.Template]: '#4a4a4a',
  [AssetNodeType.Collection]: '#af302c',
  [AssetNodeType.Group]: '#f59e0b',
  [AssetNodeType.Item]: '#22c55e',
};

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

interface AssetDetailHeaderProps {
  node: any;
  breadcrumb: BreadcrumbItem[];
  templateName: string;
  onSelectNode: (nodeId: string) => void;
  onDuplicate: () => void;
  onMove: () => void;
  onAddNew: () => void;
  onAddTemplate: () => void;
  onAddGroup: () => void;
  onAddAsset: () => void;
  onEditGroup?: (group: any) => void;
  onEditAsset?: (item: any) => void;
  onEditTemplate?: (template: any) => void;
  onArchiveNode?: (node: any) => void;
  onUnarchiveNode?: (node: any) => void;
}

const AssetDetailHeader: React.FC<AssetDetailHeaderProps> = ({
  node,
  breadcrumb,
  templateName,
  onSelectNode,
  onDuplicate,
  onMove,
  onAddNew,
  onAddTemplate,
  onAddGroup,
  onAddAsset,
  onEditGroup,
  onEditAsset,
  onEditTemplate,
  onArchiveNode,
  onUnarchiveNode,
}) => {
  const intl = useIntl();
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);

  const typeColor = node
    ? nodeTypeColors[node.type] || nodeTypeColors[AssetNodeType.Item]
    : nodeTypeColors[AssetNodeType.Template];
  const pathItems = breadcrumb.slice(1);
  const typeLabel = node
    ? node.type === AssetNodeType.Group
      ? 'GROUP'
      : node.type === AssetNodeType.Item
      ? 'ITEM'
      : intl.formatMessage({ id: 'assets.detail.template' })
    : intl.formatMessage({ id: 'assets.detail.template' });

  const isArchived = Boolean((node?.raw || node)?.archivedAt);

  const editHandlerByType: Record<string, ((raw: any) => void) | undefined> = {
    [AssetNodeType.Group]: onEditGroup,
    [AssetNodeType.Item]: onEditAsset,
    [AssetNodeType.Template]: onEditTemplate,
  };
  const onEdit = node ? editHandlerByType[node.type] : undefined;

  const addComponentDropdown = (
    <Dropdown isOpen={addDropdownOpen} toggle={() => setAddDropdownOpen((prev) => !prev)}>
      <DropdownToggle color="primary" size="sm" caret>
        <i className="uil uil-plus mr-1" />
        {intl.formatMessage({ id: 'assets.actions.addComponent' })}
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={onAddTemplate}>
          <i className="uil uil-file-alt mr-2" />
          {intl.formatMessage({ id: 'assets.actions.newTemplate' })}
        </DropdownItem>
        <DropdownItem onClick={onAddGroup}>
          <i className="uil uil-folder mr-2" />
          {intl.formatMessage({ id: 'assets.actions.newGroup' })}
        </DropdownItem>
        <DropdownItem onClick={onAddAsset}>
          <i className="uil uil-box mr-2" />
          {intl.formatMessage({ id: 'assets.actions.newAsset' })}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );

  return (
    <div className="asset-detail-header">
      <div className="asset-detail-header__top-bar">
        {templateName && (
          <div className="asset-detail-header__template-info">
            <span className="asset-detail-header__template-label">{typeLabel}</span>
            <span className="asset-detail-header__template-name">{templateName}</span>
          </div>
        )}

        {node && pathItems.length > 0 && (
          <div className="asset-detail-header__breadcrumb">
            {pathItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && (
                  <span className="asset-detail-header__breadcrumb-separator">{'\u203A'}</span>
                )}
                <button
                  className={`asset-detail-header__breadcrumb-item ${
                    item.id === node.id ? 'asset-detail-header__breadcrumb-item--active' : ''
                  }`}
                  onClick={() => onSelectNode(item.id)}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="asset-detail-header__actions">{addComponentDropdown}</div>
      </div>

      {node && (
        <div className="asset-detail-header__node-info">
          <span
            className="asset-detail-header__node-indicator"
            style={{ backgroundColor: typeColor }}
          />
          <div className="asset-detail-header__node-details">
            <span className="asset-detail-header__node-type">{node.type.toUpperCase()}</span>
            <h2 className="asset-detail-header__node-name">
              {node.name}
              {isArchived && (
                <span className="badge badge-secondary ml-2 align-middle">
                  {intl.formatMessage({ id: 'assets.archive.archivedBadge' })}
                </span>
              )}
            </h2>
            {node.type === AssetNodeType.Item && (
              <p className="asset-detail-header__node-description">
                {intl.formatMessage(
                  { id: 'assets.detail.partOfGroup' },
                  {
                    group: breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].name : '',
                    collection: breadcrumb.length > 2 ? breadcrumb[breadcrumb.length - 3].name : '',
                  }
                )}
              </p>
            )}
          </div>

          <div className="asset-detail-header__node-actions">
            {onEdit && (
              <UserGroupPermissions itemRef={editPermissionRefs.assets}>
                <Button outline color="primary" size="sm" onClick={() => onEdit(node.raw || node)}>
                  <i className="uil uil-edit mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.edit' })}
                </Button>
              </UserGroupPermissions>
            )}
            {!isArchived && onArchiveNode && (
              <UserGroupPermissions itemRef={archivePermissionRefs.assets}>
                <Button outline color="secondary" size="sm" onClick={() => onArchiveNode(node)}>
                  <i className="uil uil-archive mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.archive' })}
                </Button>
              </UserGroupPermissions>
            )}
            {isArchived && onUnarchiveNode && (
              <UserGroupPermissions itemRef={archivePermissionRefs.assets}>
                <Button outline color="primary" size="sm" onClick={() => onUnarchiveNode(node)}>
                  <i className="uil uil-history mr-1" />
                  {intl.formatMessage({ id: 'assets.actions.unarchive' })}
                </Button>
              </UserGroupPermissions>
            )}
            <Button outline color="secondary" size="sm" onClick={onDuplicate}>
              {intl.formatMessage({ id: 'assets.actions.duplicate' })}
            </Button>
            <Button outline color="secondary" size="sm" onClick={onMove}>
              {intl.formatMessage({ id: 'assets.actions.move' })}
            </Button>
            <Button color="primary" size="sm" onClick={onAddNew}>
              <i className="uil uil-plus mr-1" />
              {intl.formatMessage({ id: 'assets.actions.addNew' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetDetailHeader;
